import React, { useState, useEffect } from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
    TablePagination, Chip, Box, TextField, MenuItem, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, Typography, Button, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

// Компонент для отображения детальной информации о конверсии
const ConversionDetailsDialog = ({ open, onClose, conversionData }) => {
    if (!conversionData) return null;
    
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Детальная информация о конверсии
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Данные конверсии:
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                            {JSON.stringify(conversionData, null, 2)}
                        </pre>
                    </Paper>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

// Компонент для отображения статуса с цветовым индикатором
const StatusChip = ({ status }) => {
    let color = 'default';
    let label = status || 'unknown';
    
    switch (status) {
        case 'confirmed':
            color = 'success';
            label = 'Подтвержден';
            break;
        case 'rejected':
            color = 'error';
            label = 'Отклонен';
            break;
        case 'hold':
            color = 'warning';
            label = 'Холд';
            break;
        case 'trashed':
            color = 'error';
            label = 'Треш';
            break;
        case 'unknown':
            color = 'default';
            label = 'Без статуса';
            break;
        default:
            // Для других статусов сохраняем оригинальное значение
            color = 'default';
    }
    
    return <Chip label={label} color={color} size="small" />;
};

const ConversionLogs = () => {
    // Состояние для хранения данных
    const [conversions, setConversions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Состояние для пагинации
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalItems, setTotalItems] = useState(0);
    
    // Состояние для фильтров
    const [statusFilter, setStatusFilter] = useState('');
    const [siteIdFilter, setSiteIdFilter] = useState('');
    
    // Состояние для диалога с деталями
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [selectedConversionData, setSelectedConversionData] = useState(null);
    
    // Функция для загрузки данных
    const fetchConversions = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Не найден токен авторизации');
            }
            
            // Подготовка параметров запроса
            const queryParams = new URLSearchParams();
            
            // Пагинация
            queryParams.append('page', page + 1);
            queryParams.append('limit', rowsPerPage);
            
            // Опционально добавляем фильтр по статусу
            if (statusFilter) {
                queryParams.append('status', statusFilter);
            }
            
            // Опционально добавляем фильтр по ID сайта
            if (siteIdFilter) {
                queryParams.append('site_id', siteIdFilter);
            }
            
            // Даты по умолчанию: последние 30 дней
            const today = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(today.getDate() - 30);
            
            queryParams.append('startDate', thirtyDaysAgo.toISOString().split('T')[0]); // Формат YYYY-MM-DD
            queryParams.append('endDate', today.toISOString().split('T')[0]); // Формат YYYY-MM-DD
            
            const url = `/api/admin/stats/conversions?${queryParams.toString()}`;
            console.log('Отправка запроса на:', url);
            
            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            
            console.log('Ответ от сервера:', response.data);
            
            if (response.data.success) {
                // Проверяем структуру полученных данных
                const conversionsData = response.data.data.conversions || [];
                console.log('Получено конверсий:', conversionsData.length);
                
                setConversions(conversionsData);
                
                // Устанавливаем общее количество элементов для пагинации из ответа
                if (response.data.data.pagination && response.data.data.pagination.total !== undefined) {
                    setTotalItems(response.data.data.pagination.total);
                } else {
                    setTotalItems(conversionsData.length);
                }
            } else {
                throw new Error(response.data.message || 'Ошибка при загрузке данных');
            }
        } catch (err) {
            console.error('Ошибка при загрузке логов конверсий:', err);
            setError(err.response?.data?.message || err.message || 'Ошибка при загрузке логов конверсий');
        } finally {
            setLoading(false);
        }
    };
    
    // Загрузка данных при изменении параметров
    useEffect(() => {
        fetchConversions();
    }, [page, rowsPerPage, statusFilter, siteIdFilter]);
    
    // Обработчики пагинации
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    // Обработчик изменения фильтра статуса
    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };
    
    // Обработчик изменения фильтра ID сайта
    const handleSiteIdFilterChange = (event) => {
        setSiteIdFilter(event.target.value);
        setPage(0);
    };
    
    // Обработчики для диалога
    const handleOpenDetails = (conversionData) => {
        setSelectedConversionData(conversionData);
        setDetailsDialogOpen(true);
    };
    
    const handleCloseDetails = () => {
        setDetailsDialogOpen(false);
    };
    
    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return format(new Date(dateString), 'dd.MM.yyyy, HH:mm:ss', { locale: ru });
    };
    
    // Форматирование суммы
    const formatAmount = (amount, currency) => {
        if (amount === null || amount === undefined) return '-';
        return `${amount}${currency ? ' ' + currency : ''}`;
    };
    
    // Статусы для фильтра
    const statusOptions = [
        { value: '', label: 'Все статусы' },
        { value: 'confirmed', label: 'Подтверждены' },
        { value: 'rejected', label: 'Отклонены' },
        { value: 'hold', label: 'Холды' },
        { value: 'trashed', label: 'Треш' },
        { value: 'unknown', label: 'Без статуса' }
    ];
    
    return (
        <Box>
            {/* Фильтры */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <TextField
                    select
                    label="Статус"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    size="small"
                    sx={{ minWidth: 200 }}
                >
                    {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                
                <TextField
                    label="ID сайта"
                    value={siteIdFilter}
                    onChange={handleSiteIdFilterChange}
                    size="small"
                    placeholder="Введите ID сайта"
                    sx={{ minWidth: 200 }}
                    InputProps={{
                        endAdornment: siteIdFilter ? (
                            <IconButton
                                size="small"
                                onClick={() => setSiteIdFilter('')}
                                edge="end"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        ) : null
                    }}
                />
            </Box>
            
            {/* Информация о фильтрации */}
            {siteIdFilter && (
                <Box sx={{ mb: 2 }}>
                    <Chip 
                        label={`Отфильтровано по ID сайта: ${siteIdFilter}`} 
                        color="primary" 
                        onDelete={() => setSiteIdFilter('')}
                        sx={{ mr: 1 }}
                    />
                </Box>
            )}
            
            {/* Сообщение об ошибке */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            {/* Таблица с данными */}
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Дата</TableCell>
                            <TableCell>Click ID</TableCell>
                            <TableCell>ID сайта</TableCell>
                            <TableCell>Новость</TableCell>
                            <TableCell>Сумма</TableCell>
                            <TableCell>Валюта</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell>Данные конверсии</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={30} />
                                </TableCell>
                            </TableRow>
                        ) : conversions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    Нет данных для отображения
                                </TableCell>
                            </TableRow>
                        ) : (
                            conversions.map((item) => (
                                <TableRow key={item.id || item._id}>
                                    <TableCell>{formatDate(item.date)}</TableCell>
                                    <TableCell>{item.click_id || '-'}</TableCell>
                                    <TableCell>
                                        {item.site_id ? (
                                            <Chip 
                                                label={item.site_id} 
                                                size="small" 
                                                variant="outlined" 
                                                color="primary" 
                                                clickable
                                                onClick={() => {
                                                    setSiteIdFilter(item.site_id);
                                                    setPage(0);
                                                }}
                                                title="Нажмите, чтобы фильтровать по этому ID сайта"
                                            />
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell>{item.newsTitle || '-'}</TableCell>
                                    <TableCell>{formatAmount(item.payout, null)}</TableCell>
                                    <TableCell>{item.currency || '-'}</TableCell>
                                    <TableCell>
                                        <StatusChip status={item.status} />
                                    </TableCell>
                                    <TableCell>
                                        {item.conversionData && Object.keys(item.conversionData).length > 0 ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        mr: 1
                                                    }}
                                                >
                                                    {JSON.stringify(item.conversionData).substring(0, 50)}
                                                    {JSON.stringify(item.conversionData).length > 50 ? '...' : ''}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => handleOpenDetails(item.conversionData)}
                                                    title="Показать все данные"
                                                >
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {/* Пагинация */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Строк на странице:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
            />
            
            {/* Диалог с деталями конверсии */}
            <ConversionDetailsDialog 
                open={detailsDialogOpen} 
                onClose={handleCloseDetails}
                conversionData={selectedConversionData}
            />
        </Box>
    );
};

export default ConversionLogs; 